import { type DragEvent, type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ExternalLink,
  HelpCircle,
  ImageIcon,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  RotateCcw,
  SendHorizonal,
  Trash2,
  X,
  XCircle,
  Zap,
} from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { Pagination } from "@/components/ui/pagination";
import { EmptyPanel, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import TicketAttachment from "@/components/TicketAttachment";
import { CommentImage, NivaAckBubble, PriorityBadge, ReplyAttachmentButton, StatusBadge, threadWithNiva } from "@/components/SupportTicketParts";
import {
  CATEGORIES,
  CATEGORY_ICON,
  PRIORITIES,
  PRIORITY_BORDER,
  type TicketsPage,
  fmtDate,
  fmtDateTime,
  uploadTicketImage,
} from "@/lib/supportTickets";
import { cn } from "@/lib/utils";
import type { SupportTicket } from "@/types";

// ─── conversation bubbles ──────────────────────────────────────────────────────

function OwnerBubble({ name, at, text, editedAt, imageUrl, onEdit }: { name: string; at: string; text: string; editedAt?: string | null; imageUrl?: string | null; onEdit?: () => void }) {
  return (
    <div className="flex items-end justify-end gap-2.5">
      <div className="max-w-[85%] space-y-1">
        <p className="text-[10px] font-medium text-muted-foreground text-right pr-1">
          {name} · {fmtDateTime(at)}{editedAt && <span> · edited</span>}
        </p>
        <div className="flex flex-row-reverse items-end gap-1.5">
          <div className="flex flex-col items-end">
            <div className="rounded-2xl rounded-br-sm bg-primary px-4 py-3 text-sm text-primary-foreground leading-relaxed whitespace-pre-wrap shadow-sm">
              {text}
            </div>
            {imageUrl && <CommentImage url={imageUrl} />}
          </div>
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              title="Edit message"
              aria-label="Edit message"
              className="mb-1 shrink-0 h-6 w-6 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground flex items-center justify-center opacity-50 hover:opacity-100 transition-all"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary text-[10px] font-bold mb-0.5">
        {name.charAt(0).toUpperCase()}
      </div>
    </div>
  );
}

function SupportBubble({ name, at, text, editedAt, imageUrl }: { name: string; at: string; text: string; editedAt?: string | null; imageUrl?: string | null }) {
  return (
    <div className="flex items-end gap-2.5">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-foreground/70 ring-1 ring-border/60 text-[10px] font-bold mb-0.5">
        {name.charAt(0).toUpperCase()}
      </div>
      <div className="max-w-[85%] space-y-1">
        <p className="text-[10px] font-medium text-muted-foreground pl-1">
          {name} · {fmtDateTime(at)}{editedAt && <span> · edited</span>}
        </p>
        <div className="rounded-2xl rounded-bl-sm bg-muted/60 border border-border/50 px-4 py-3 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap shadow-sm">
          {text}
        </div>
        {imageUrl && <CommentImage url={imageUrl} />}
      </div>
    </div>
  );
}

// ─── modal shell ─────────────────────────────────────────────────────────────

function Modal({ children, onClose, wide, tall }: { children: ReactNode; onClose: () => void; wide?: boolean; tall?: boolean }) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-1 sm:p-2"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className={cn(
        "relative w-full rounded-2xl border border-border/80 bg-card shadow-2xl flex flex-col max-h-[99dvh]",
        wide ? "max-w-6xl" : "max-w-lg",
        // A chat/form needs a stable tall frame so the body scrolls under a fixed footer.
        tall && "h-[99dvh]",
      )}>
        {children}
      </div>
    </div>
  );
}

// ─── new ticket modal ─────────────────────────────────────────────────────────

function NewTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: (t: SupportTicket) => void }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("medium");
  // The stored blob URL — the image is uploaded as soon as it's picked, so
  // the ticket itself only ever carries a URL.
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  async function attach(file: File) {
    setUploading(true);
    try {
      setImageUrl(await uploadTicketImage(file));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload image.");
    } finally {
      setUploading(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) attach(file);
    e.target.value = "";
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) attach(file);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) { toast.error("Subject and description are required."); return; }
    setSubmitting(true);
    try {
      const created = await api.post<SupportTicket>("/support/tickets", {
        subject: subject.trim(), description: description.trim(), category, priority, image_url: imageUrl,
      });
      onCreated(created);
      toast.success("Ticket submitted! Our team will get back to you.");
    } catch {
      toast.error("Failed to submit ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} wide tall>
      {/* header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/60 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Plus className="h-4 w-4" />
          </div>
          <h2 className="text-base font-bold text-foreground">New Support Ticket</h2>
        </div>
        <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* body — scrolls under a fixed footer */}
      <form onSubmit={handleSubmit} className="flex flex-1 min-h-0 flex-col">
        <div className="flex flex-col gap-5 overflow-y-auto px-6 py-5">
        <div className="space-y-1.5">
          <Label htmlFor="subject">Subject</Label>
          <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary of your issue" maxLength={255} required />
        </div>

        <div className="space-y-2">
          <Label>Category</Label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const selected = category === c.value;
              return (
                <button key={c.value} type="button" onClick={() => setCategory(c.value)}
                  className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors", selected ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground")}>
                  <Icon className="h-3 w-3" />{c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Priority</Label>
          <div className="flex flex-wrap gap-2">
            {PRIORITIES.map((p) => {
              const selected = priority === p.value;
              return (
                <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                  className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all", p.color, selected ? "ring-2 ring-offset-1 ring-current font-semibold" : "opacity-60 hover:opacity-90")}>
                  {p.value === "urgent" && <Zap className="h-3 w-3" />}
                  {p.value === "high" && <AlertCircle className="h-3 w-3" />}
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your issue in detail — the more context you provide, the faster we can help."
            rows={7} maxLength={10000} required />
        </div>

        {/* image upload */}
        <div className="space-y-2">
          <Label>Attachment <span className="text-muted-foreground font-normal">(optional)</span></Label>
          {imageUrl ? (
            <div className="relative rounded-xl border border-border/60 overflow-hidden">
              <img src={imageUrl} alt="attachment preview" className="w-full max-h-48 object-contain bg-muted/20" />
              <button
                type="button"
                aria-label="Remove image"
                onClick={() => setImageUrl(null)}
                className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-background/90 border border-border/70 text-muted-foreground shadow-sm hover:text-destructive hover:border-destructive/50 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
              className={cn(
                "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 cursor-pointer transition-colors",
                uploading && "pointer-events-none opacity-70",
                dragging ? "border-primary bg-primary/5" : "border-border/60 bg-muted/10 hover:border-primary/40 hover:bg-muted/20",
              )}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted/60 text-muted-foreground">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">{uploading ? "Uploading…" : "Drop an image here"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">or click to browse · PNG, JPG, GIF, WEBP up to 5 MB</p>
              </div>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={handleFileChange} />
            </div>
          )}
        </div>
        </div>

        {/* footer — fixed at the bottom so Submit is always visible */}
        <div className="shrink-0 flex justify-end gap-3 border-t border-border/60 px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting || uploading} className="gap-2">
            {submitting ? "Submitting…" : <><Plus className="h-3.5 w-3.5" />Submit Ticket</>}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── view ticket modal ────────────────────────────────────────────────────────

function ViewTicketModal({ ticket, onClose, onUpdated }: { ticket: SupportTicket; onClose: () => void; onUpdated: (t: SupportTicket) => void }) {
  const [tab, setTab] = useState<"conversation" | "attachment">("conversation");
  const [current, setCurrent] = useState(ticket);
  // List rows leave the attachment out (has_image only), so the full ticket is
  // fetched on open. That also covers opening straight from a ?ticket= link.
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyImage, setReplyImage] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const CategoryIcon = CATEGORY_ICON[current.category] ?? HelpCircle;
  const isClosed = current.status === "closed";
  const isResolved = current.status === "resolved";
  const hasSupportReply = current.comments.some((c) => c.admin_id !== current.user_id);

  useEffect(() => {
    let cancelled = false;
    api.get<SupportTicket>(`/support/tickets/${ticket.id}`)
      .then((full) => { if (!cancelled) setCurrent(full); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [ticket.id]);

  // Keep the newest message in view as the thread grows / when opening the tab.
  useEffect(() => {
    if (tab === "conversation") bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [current.comments.length, tab]);

  const tabs = [
    { key: "conversation" as const, label: "Conversation", Icon: MessageSquare, badge: 1 + current.comments.length },
    { key: "attachment" as const, label: "Attachment", Icon: ImageIcon, badge: current.has_image ? 1 : null },
  ];

  function apply(updated: SupportTicket) {
    setCurrent(updated);
    onUpdated(updated);
  }

  async function handleReply(e?: FormEvent) {
    e?.preventDefault();
    if (!replyText.trim()) return;
    setSending(true);
    try {
      apply(await api.post<SupportTicket>(`/support/tickets/${current.id}/comments`, {
        comment: replyText.trim(),
        image_url: replyImage,
      }));
      setReplyText("");
      setReplyImage(null);
    } catch {
      toast.error("Failed to send your message. Please try again.");
    } finally {
      setSending(false);
    }
  }

  async function setStatus(status: "open" | "closed") {
    if (status === "closed" && !window.confirm("Close this ticket? You can reopen it later if you still need help.")) return;
    setChangingStatus(true);
    try {
      apply(await api.patch<SupportTicket>(`/support/tickets/${current.id}`, { status }));
      toast.success(status === "closed" ? "Ticket closed." : "Ticket reopened.");
    } catch {
      toast.error(status === "closed" ? "Failed to close the ticket." : "Failed to reopen the ticket.");
    } finally {
      setChangingStatus(false);
    }
  }

  const startEdit = (id: string, text: string) => { setEditingId(id); setEditText(text); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };

  async function saveEdit(commentId: string, original: string) {
    const next = editText.trim();
    if (!next || savingEdit) return;
    if (next === original) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      apply(await api.patch<SupportTicket>(`/support/tickets/${current.id}/comments/${commentId}`, { comment: next }));
      cancelEdit();
    } catch {
      toast.error("Failed to update your message. Please try again.");
    } finally {
      setSavingEdit(false);
    }
  }

  return (
    <Modal onClose={onClose} wide tall>
      {/* ── Header ── */}
      <div className="relative shrink-0 overflow-hidden rounded-t-2xl border-b border-primary/20 bg-gradient-to-br from-primary/12 via-card to-card px-6 py-5">
        {/* soft gold glow accent */}
        <div className="pointer-events-none absolute -right-20 -top-24 h-52 w-52 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary ring-1 ring-primary/25">
              <CategoryIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="text-xl font-bold text-foreground leading-tight">{current.subject}</h2>
                <span className="inline-flex items-center rounded-md bg-primary/12 px-2 py-0.5 text-xs font-mono font-semibold text-primary ring-1 ring-primary/25 whitespace-nowrap shrink-0">
                  {current.ticket_id}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground capitalize">
                {current.category} · Submitted {fmtDate(current.created_at)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-foreground/5 text-muted-foreground ring-1 ring-border/60 hover:bg-foreground/10 hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative mt-4 flex flex-wrap items-center gap-2">
          <StatusBadge value={current.status} />
          <PriorityBadge value={current.priority} />
          {current.comments.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground/5 px-2.5 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border/60">
              <MessageSquare className="h-3 w-3" />
              {current.comments.length} {current.comments.length === 1 ? "message" : "messages"}
            </span>
          )}
          {!isClosed && (
            <button
              type="button"
              onClick={() => setStatus("closed")}
              disabled={changingStatus}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-medium text-muted-foreground hover:border-destructive/50 hover:text-destructive disabled:opacity-50 transition-colors"
            >
              <XCircle className="h-3.5 w-3.5" />
              Close ticket
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex shrink-0 border-b border-border/60 px-6 bg-card">
        {tabs.map(({ key, label, Icon, badge }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              "mr-6 inline-flex items-center gap-1.5 border-b-2 py-3 text-xs font-semibold transition-colors",
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
            {badge !== null && (
              <span className={cn(
                "rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none",
                tab === key ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
              )}>
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Conversation tab — scrollable thread + sticky composer ── */}
      {tab === "conversation" && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Message thread (scrolls) */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-5 space-y-4">
            <OwnerBubble name="You" at={current.created_at} text={current.description} />
            {threadWithNiva(current).map((c) =>
              c.kind === "niva" ? (
                <NivaAckBubble key={c.id} text={c.text} at={c.created_at} />
              ) : c.admin_id === current.user_id ? (
                editingId === c.id ? (
                  <div key={c.id} className="flex items-end justify-end gap-2.5">
                    <div className="w-full max-w-[85%] flex flex-col gap-1.5">
                      <Textarea
                        autoFocus
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(c.id, c.comment); }
                          if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                        }}
                        rows={3}
                        className="resize-none text-sm"
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button type="button" onClick={cancelEdit} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors">Cancel</button>
                        <Button type="button" size="sm" disabled={!editText.trim() || savingEdit} onClick={() => saveEdit(c.id, c.comment)} className="h-7 px-3 text-[11px]">Save</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <OwnerBubble
                    key={c.id}
                    name="You"
                    at={c.created_at}
                    text={c.comment}
                    editedAt={c.edited_at}
                    imageUrl={c.image_url}
                    onEdit={isClosed ? undefined : () => startEdit(c.id, c.comment)}
                  />
                )
              ) : (
                <SupportBubble key={c.id} name={c.admin_name ?? "Support Team"} at={c.created_at} text={c.comment} editedAt={c.edited_at} imageUrl={c.image_url} />
              ),
            )}

            {/* Awaiting first support reply */}
            {!hasSupportReply && (
              <div className="rounded-xl border border-dashed border-border/70 bg-muted/10 py-8 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-muted/50 mb-2.5">
                  <MessageSquare className="h-5 w-5 text-muted-foreground/50" />
                </div>
                <p className="text-sm font-medium text-muted-foreground">No responses yet</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Our support team will reply as soon as possible.</p>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Composer (fixed at the bottom) — available until the ticket is closed */}
          {isClosed ? (
            <div className="shrink-0 border-t border-border/60 px-5 sm:px-6 py-4">
              <div className="flex flex-col items-center gap-2 rounded-xl border border-border/60 bg-muted/20 px-4 py-3 text-center text-xs text-muted-foreground sm:flex-row sm:justify-between sm:text-left">
                <span>This ticket is closed. Reopen it if you still need help.</span>
                <Button type="button" size="sm" variant="outline" disabled={changingStatus} onClick={() => setStatus("open")} className="h-7 gap-1.5 px-3 text-[11px]">
                  <RotateCcw className="h-3 w-3" />
                  Reopen ticket
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleReply} className="shrink-0 border-t border-border/60 bg-card px-5 sm:px-6 py-3.5">
              <div className="flex items-end gap-2.5">
                <Textarea
                  id="reply"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleReply(); }
                  }}
                  placeholder="Reply to support or add more details…"
                  rows={2}
                  className="flex-1 resize-none min-h-[52px]"
                />
                <Button type="submit" disabled={sending || !replyText.trim()} className="gap-2 shrink-0">
                  <SendHorizonal className="h-3.5 w-3.5" />
                  {sending ? "Sending…" : "Send"}
                </Button>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                <ReplyAttachmentButton value={replyImage} onChange={setReplyImage} disabled={sending} />
                <p className="text-[11px] text-muted-foreground">
                  {isResolved ? "Replying will reopen this ticket · " : ""}⌘/Ctrl + Enter to send
                </p>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── Attachment tab ── */}
      {tab === "attachment" && (
        <div className="flex-1 min-h-0 flex flex-col overflow-y-auto px-6 py-5">
          {loadingDetail && current.has_image && !current.image_url ? (
            <div className="flex justify-center py-20"><Loader /></div>
          ) : (
            <TicketAttachment
              url={current.image_url}
              label="Attached image"
              emptyHint="You did not include an image with this ticket."
            />
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── tickets table ────────────────────────────────────────────────────────────

function TicketsTable({
  tickets,
  onView,
}: {
  tickets: SupportTicket[];
  onView: (t: SupportTicket) => void;
}) {

  return (
    <div>
      <div className="overflow-x-auto -mx-5 sm:-mx-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30">
              <th className="py-3 pl-5 sm:pl-6 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden sm:table-cell">Ticket ID</th>
              <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Subject</th>
              <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden sm:table-cell">Category</th>
              <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
              <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden md:table-cell">Priority</th>
              <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden lg:table-cell">Submitted</th>
              <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden md:table-cell">Responses</th>
              <th className="py-3 pl-3 pr-5 sm:pr-6 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {tickets.map((ticket) => {
              const CategoryIcon = CATEGORY_ICON[ticket.category] ?? HelpCircle;
              return (
                <tr key={ticket.id} className={cn("group transition-colors hover:bg-muted/25 border-l-4", PRIORITY_BORDER[ticket.priority] ?? "border-l-transparent")}>
                  <td className="py-3.5 pl-5 sm:pl-6 pr-3 hidden sm:table-cell">
                    <span className="inline-flex items-center rounded-md bg-muted/60 border border-border/60 px-2 py-0.5 text-xs font-mono font-semibold text-muted-foreground whitespace-nowrap">
                      {ticket.ticket_id}
                    </span>
                  </td>
                  <td className="py-3.5 px-3 max-w-[200px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-medium text-foreground truncate">{ticket.subject}</p>
                      {ticket.support_replied_last && (
                        <span
                          title="Support has replied and is waiting on you"
                          className="shrink-0 rounded-full bg-gold-gradient px-1.5 py-0.5 text-[0.55rem] font-black uppercase tracking-[0.08em] text-primary-foreground"
                        >
                          New reply
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate line-clamp-1 hidden sm:block md:hidden">{ticket.description}</p>
                  </td>
                  <td className="py-3.5 px-3 hidden sm:table-cell">
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground capitalize">
                      <CategoryIcon className="h-3.5 w-3.5 shrink-0" />
                      {ticket.category}
                    </span>
                  </td>
                  <td className="py-3.5 px-3">
                    <StatusBadge value={ticket.status} />
                  </td>
                  <td className="py-3.5 px-3 hidden md:table-cell">
                    <PriorityBadge value={ticket.priority} />
                  </td>
                  <td className="py-3.5 px-3 text-xs text-muted-foreground whitespace-nowrap hidden lg:table-cell">
                    {fmtDate(ticket.created_at)}
                  </td>
                  <td className="py-3.5 px-3 hidden md:table-cell">
                    {ticket.comments.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-primary font-medium">
                        <MessageSquare className="h-3 w-3" />
                        {ticket.comments.length}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/60">—</span>
                    )}
                  </td>
                  <td className="py-3.5 pl-3 pr-5 sm:pr-6 text-right">
                    <button
                      type="button"
                      onClick={() => onView(ticket)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary group-hover:border-primary/30"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

export default function SupportTicketsPage() {
  const [data, setData] = useState<TicketsPage | null>(null);
  const [counts, setCounts] = useState<{ total: number; active: number; resolved: number } | null>(null);
  const [loading, setLoading] = useState(true);
  // Page lives in the URL (?page=N) so navigating away and coming back returns
  // to the same page instead of resetting to the first one. ?ticket=<id> opens
  // that ticket — it's where notifications and emails link to.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const linkedTicketId = searchParams.get("ticket");
  const setParam = (key: string, value: string | null) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value) p.set(key, value);
        else p.delete(key);
        return p;
      },
      { replace: true },
    );
  };
  const setPage = (next: number) => setParam("page", next > 1 ? String(next) : null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [viewTicket, setViewTicket] = useState<SupportTicket | null>(null);

  function fetchPage(p: number) {
    setLoading(true);
    api.get<TicketsPage>(`/support/tickets?page=${p}&page_size=${PAGE_SIZE}`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  function fetchCounts() {
    api.get<{ total: number; active: number; resolved: number }>("/support/tickets/counts")
      .then(setCounts)
      .catch(() => {});
  }

  useEffect(() => { fetchPage(page); }, [page]);
  useEffect(() => { fetchCounts(); }, []);

  // Open the ticket a notification linked to. The modal fetches the full
  // ticket itself; a stub with just the id is enough to mount it.
  useEffect(() => {
    if (!linkedTicketId || viewTicket?.id === linkedTicketId) return;
    api.get<SupportTicket>(`/support/tickets/${linkedTicketId}`)
      .then(setViewTicket)
      .catch(() => setParam("ticket", null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedTicketId]);

  function openTicket(t: SupportTicket) {
    setViewTicket(t);
    setParam("ticket", t.id);
  }

  function closeTicket() {
    setViewTicket(null);
    setParam("ticket", null);
  }

  function handleTicketUpdated(updated: SupportTicket) {
    setData((prev) =>
      prev ? { ...prev, items: prev.items.map((t) => (t.id === updated.id ? updated : t)) } : prev,
    );
    fetchCounts();
  }

  const total = counts?.total ?? data?.total ?? 0;

  return (
    <PageShell>
      <PageHero
        eyebrow="Help Center"
        title="Support Tickets"
        description="Submit a request to our team and track your open issues."
        actions={
          <Button onClick={() => setShowNewModal(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Ticket
          </Button>
        }
        aside={
          counts && counts.total > 0 ? (
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-extrabold text-foreground">{counts.total}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Total</p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-amber-600">{counts.active}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Active</p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-emerald-600">{counts.resolved}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Resolved</p>
              </div>
            </div>
          ) : undefined
        }
      />

      <PagePanel>
        <PanelHeading
          title="My Tickets"
          description={total > 0 ? `${total} ticket${total !== 1 ? "s" : ""} submitted` : undefined}
        />

        {loading ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : !data || data.items.length === 0 ? (
          <EmptyPanel
            icon={MessageSquare}
            title="No support tickets yet"
            description="Submit a ticket if you need help with anything — our team typically responds within 24 hours."
          />
        ) : (
          <>
            <TicketsTable
              tickets={data.items}
              onView={openTicket}
            />
            <Pagination
              page={page}
              totalElements={data.total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              className="mt-1 px-5 sm:px-6"
            />
          </>
        )}
      </PagePanel>

      {showNewModal && (
        <NewTicketModal
          onClose={() => setShowNewModal(false)}
          onCreated={() => { setPage(1); fetchPage(1); fetchCounts(); setShowNewModal(false); }}
        />
      )}

      {viewTicket && (
        <ViewTicketModal key={viewTicket.id} ticket={viewTicket} onClose={closeTicket} onUpdated={handleTicketUpdated} />
      )}
    </PageShell>
  );
}
